import type { FC, ReactNode } from 'react'
import type { WashPackage } from '@/types'

import Image from 'next/image'
import Link from 'next/link'

import {
    ArrowRight,
    CalendarCheck,
    Clock,
    Hand,
    ShieldCheck,
    Sparkles
} from 'lucide-react'

import { buttonStyles } from '@/lib/button-styles'
import { getStartingPackage } from '@/lib/pricing'
import { countOpenDays } from '@/lib/schedule'
import { Bubble } from '@/components/generals/bubble'
import { Container } from '@/components/generals/container'
import { OpenStatus } from '@/components/generals/open-status'
import { WaveDivider } from '@/components/generals/wave-divider'
import { business } from '@/constants/business'

interface HeroSectionProps {
    packages: Array<WashPackage>
}

export const HeroSection: FC<HeroSectionProps> = ({ packages }): ReactNode => {
    const starter = getStartingPackage(packages)
    const openDays = countOpenDays()

    return (
        <section id='hero' aria-labelledby='hero-title' className='relative overflow-hidden bg-linear-to-b from-foam via-foam to-background'>
            <Bubble className='left-[3%] top-24 size-9 opacity-80' />
            <Bubble className='right-[4%] top-6 size-20 [animation-delay:-2s]' />
            <Bubble className='bottom-28 left-[46%] hidden size-12 [animation-delay:-5s] lg:block' />

            <Container className='relative grid items-center gap-16 pb-20 pt-10 sm:pt-14 lg:grid-cols-[1.02fr_1fr] lg:gap-12 lg:pb-28 lg:pt-20'>
                <div>
                    <p className='inline-flex items-center gap-2 rounded-full bg-white px-3.5 py-1.5 text-[0.7rem] font-bold uppercase tracking-[0.18em] text-aqua-deep shadow-sm ring-1 ring-ink/5'>
                        <Sparkles className='size-3.5' />
                        {business.tagline}
                    </p>

                    <h1
                        id='hero-title'
                        className='mt-6 font-heading text-[3.3rem] font-bold uppercase leading-[0.9] tracking-tight text-ink sm:text-7xl xl:text-[5.6rem]'
                    >
                        Drive in dusty.
                        <span className='block text-aqua-deep'>
                            Drive out gleaming.
                        </span>
                    </h1>

                    <p className='mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground sm:text-xl'>
                        Snow foam, careful two-bucket hand washing and proper valeting from a local team that sweats the small stuff. Book a slot in under a minute, or just drive in.
                    </p>

                    <div className='mt-9 flex flex-wrap gap-3'>
                        <Link href='/book' className={buttonStyles({ size: 'lg' })}>
                            Book a wash
                            <ArrowRight className='size-5' />
                        </Link>

                        <Link href='/#packages' className={buttonStyles({ variant: 'outline', size: 'lg' })}>
                            See prices
                        </Link>
                    </div>

                    <ul className='mt-10 grid gap-x-6 gap-y-3 text-[0.95rem] font-medium text-ink sm:grid-cols-3'>
                        <li className='flex items-center gap-2.5'>
                            <ShieldCheck className='size-5 shrink-0 text-aqua-deep' />
                            Paint-safe products
                        </li>

                        <li className='flex items-center gap-2.5'>
                            <Hand className='size-5 shrink-0 text-aqua-deep' />
                            Hand wash, no brushes
                        </li>

                        <li className='flex items-center gap-2.5'>
                            <CalendarCheck className='size-5 shrink-0 text-aqua-deep' />
                            Open {openDays} days a week
                        </li>
                    </ul>
                </div>

                <div className='relative mx-auto w-full max-w-xl lg:max-w-none'>
                    <div className='relative aspect-[16/11] overflow-hidden rounded-[2rem] bg-muted shadow-2xl shadow-ink/15 ring-1 ring-ink/5'>
                        <Image
                            src='/images/clean-sedan.jpg'
                            alt="A freshly cleaned dark grey saloon with glossy paintwork and polished alloy wheels"
                            fill
                            sizes='(min-width: 1280px) 600px, (min-width: 1024px) 48vw, 100vw'
                            loading='eager'
                            fetchPriority='high'
                            className='object-cover'
                        />
                    </div>

                    <Bubble className='-left-6 -top-7 size-20' />
                    <Bubble className='-right-4 bottom-20 size-12 [animation-delay:-3s]' />

                    <OpenStatus className='absolute right-4 top-4 sm:right-6 sm:top-6' />

                    {starter && (
                        <div className='absolute -bottom-9 left-4 w-60 rounded-2xl bg-white p-4 shadow-xl shadow-ink/10 ring-1 ring-ink/5 sm:left-8'>
                            <p className='text-[0.68rem] font-bold uppercase tracking-[0.18em] text-muted-foreground'>
                                Washes from
                            </p>

                            <p className='mt-1 font-heading text-4xl font-bold leading-none text-ink'>
                                <span data-mdlfy-cms={`packages:${starter.id}:price`}>{starter.price}</span>
                            </p>

                            <p className='mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground'>
                                <span className='font-semibold text-ink' data-mdlfy-cms={`packages:${starter.id}:name`}>{starter.name}</span>

                                {starter.duration && (
                                    <span className='inline-flex items-center gap-1'>
                                        <Clock className='size-3.5' />
                                        <span data-mdlfy-cms={`packages:${starter.id}:duration`}>{starter.duration}</span>
                                    </span>
                                )}
                            </p>
                        </div>
                    )}
                </div>
            </Container>

            <WaveDivider className='text-ink' />
        </section>
    )
}
